/* global om */
import { createRef, Component } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import SiteSettings from '~/siteSettings';
import { verifyAnalytics, getShowfinder } from '~/utils/utils';
import LocationList from '~/components/locationList/locationList';
import PageWrapper from '~/components/wrappers/pageWrapper';
import SectionHero from '~/components/sectionHero/sectionHero';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Watch On TV
 * @category Page
 * @extends Component
 * @summary Display the watch on TV page.
 * @return {Node} React node containing the watch on TV page.
 */

class WatchOnTV extends Component {
  constructor(props) {
    super(props);
    this.state = {
      zip: '',
      isValidZip: null,
      forceZip: false, // force the invalid zip code to display if they preemptively search.
      pageInfo: {
        heading: 'Find your Station',
        subheading: 'Watch The Show',
        img: {
          alt: 'The Drew Barrymore Show',
          sm: {
            url: '/img/hero/5_mobile.jpg'
          },
          lg: {
            url: '/img/hero/5.jpg'
          }
        }
      },
      data: props.data
    };

    this.results = createRef();
  }

  componentDidMount() {
    const { query } = this.props;

    if(query && query.zip !== undefined) {
      const isValidZip = this.testZip(query.zip);

      this.setState({
        zip: query.zip,
        isValidZip,
        forceZip: false
      }, () => {
        this.submitZip();
      });
    }
  }

  getAll = async () => {
    const { zip } = this.state;
    const updatedData = await getShowfinder({
      itemsPerPage: 1000,
      offset: 0,
      ...(zip && { zip__value: parseInt(zip, 10) })
    });

    this.setState({
      data: updatedData
    });
  }

  submitZip = async () => {
    const { isValidZip, zip } = this.state;

    // Set Search Term in Utag
    const isAnalyticsVerified = await verifyAnalytics(100, 10);

    if(isAnalyticsVerified) {
      om.trackAction('trackSearchSubmit', { searchTerm: zip });
    }

    if(!isValidZip) {
      this.setState({
        forceZip: true
      });
    } else if (zip[0] === '0') {
      // If the zip starts with '0', get all results and filter on the frontend
      // This is because the backend searches for zips as integers and not strings
      const data = await getShowfinder({
        itemsPerPage: 1000,
        zip__value: parseInt(zip, 10)
      });

      const filteredResults = data.showfinderQuery.results.filter((result) => result.zip.includes(zip));

      this.setState({
        data: {
          ...data,
          showfinderQuery: {
            count: filteredResults.length,
            results: filteredResults
          }
        }
      });
    } else {
      const data = await getShowfinder({
        itemsPerPage: 10,
        offset: 0,
        zip__value: parseInt(zip, 10)
      });

      this.setState({ data });

      window.scrollTo({
        top: this.results.current.offsetTop,
        behavior: 'smooth'
      });
    }
  }

  inputZipChange = async (e) => {
    const zip = e.target.value;
    const isValidZip = this.testZip(zip);

    if(zip === '') {
      const data = await getShowfinder({
        itemsPerPage: 10,
        offset: 0
      });

      this.setState({
        data
      });
    }

    this.setState({
      zip,
      isValidZip,
      forceZip: false
    });
  }

  trackSearch = () => {
    if(window.om.trackAction) {
      window.om.trackAction('trackNewsletterSubmit');
    } else {
      // If we don't have window.om let's recursivly check for it
      this.trackTimer = setTimeout(() => {
        this.trackSubmit();
      }, 200);
    }
  }

  testZip(zip) {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zip);
  }

  render() {
    const { forceZip, zip, isValidZip, pageInfo, data } = this.state;
    const { baseUrl, sitePrimaryRsid } = this.props;
    const pageTitle = `${ pageInfo.heading } | ${ SiteSettings.siteName }`;
    const pageDesc = SiteSettings.defaultDesc;
    const path = '/watch-on-tv';

    return (
      <div className='wrap-bg-b'>
        <Head>
          <title>Where to Watch | { SiteSettings.siteName }</title>
          {/* Description */ }
          <meta name='description' content={ pageDesc } />
          <meta property='og:description' content={ pageDesc } />
          <meta name='description' content={ pageDesc } />
          <meta name='twitter:description' content={ pageDesc } />
          {/* Title */ }
          <meta name='title' content={ pageTitle } />
          <meta property='og:title' content={ pageTitle } />
          <meta name='twitter:title' content={ pageTitle } />
          {/* Others */ }
          <meta property='og:url' content={ path } />
          <link rel='canonical' href={`${ baseUrl }${ path }`} />
          <meta name="keywords" content={ SiteSettings.defaultKeywords } />
        </Head>
        <SectionHero pageInfo={ pageInfo } align='bottom'>
          { (!isValidZip && zip.length >= 5 || forceZip) &&
            <div className='error'>
              Sorry but that is not a valid zip.
            </div>
          }
          <div className='search-wrap'>
            <div className="search">
              <input
                type='text'
                placeholder='Enter Zip Code'
                id='zip'
                value={ zip }
                onChange={ this.inputZipChange }
              />
              <button
                type='submit'
                className='btn btn--brand search-button'
                onClick={ this.submitZip }
                aria-label='Search'
              >
                <span>Search</span>
              </button>
            </div>
          </div>
        </SectionHero>
        <div ref={ this.results }>
          <LocationList data={ data } zip={ zip } getAll={ this.getAll } />
        </div>
        <div className='spacer spacer--2'></div>
        <Utag pageType='showfinder' siteSection='show info' data={{ sitePrimaryRsid }} />
      </div>
    );
  }
}

export async function getStaticProps() {
  const data = await getShowfinder({
    itemsPerPage: 10,
    offset: 0
  });

  return {
    props: {
      data
    },
    revalidate: 10
  }
}

export default PageWrapper(WatchOnTV);
